import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CircularProgress from "@mui/material/CircularProgress";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_ops_assistant_confirm_action,
  API_get_learning_form_submission_for_grading,
} from "../../../constants";
import { toast } from "react-toastify";
import FormGradeModal from "../training_overview/FormGradeModal";

// Assistant card for a form submission awaiting a grade. Unlike the other
// proposal cards, the action isn't a one-click server mutation — the manager
// grades in the same FormGradeModal used elsewhere (which saves via its own
// endpoint with concurrency tokens). Only once that save succeeds do we confirm
// the proposal, whose backend executor verifies the submission is now graded.
export default function FormGradeProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  homeCompany,
  onStatusChange,
}) {
  const [status, setStatus] = useState(proposal.status || "pending");
  const [opening, setOpening] = useState(false); // fetching submission detail
  const [finalizing, setFinalizing] = useState(false); // confirming proposal
  const [gradeData, setGradeData] = useState(null); // {record, submission}

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  const summary = proposal.summary || {};

  // Pull the full submission (responses, field snapshot, scoring config) the
  // grade modal needs. The list/proposal only carries identifiers.
  const handleOpenGrader = async () => {
    if (opening || finalizing) return;
    setOpening(true);
    try {
      const res = await getRightworkAPI(
        API_get_learning_form_submission_for_grading,
        {
          company_id: homeCompany,
          learning_form_uuid: summary.form_uuid,
          section_uuid: summary.section_uuid,
          store_team_member_id: summary.store_team_member_id,
        }
      );
      const payload = res?.data?.redux?.payload;
      if (payload?.submission) {
        setGradeData({ record: payload.record, submission: payload.submission });
      } else {
        toast.info("There's no submission to grade anymore.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          error?.response?.data?.error ||
          "Couldn't load the submission to grade."
      );
    } finally {
      setOpening(false);
    }
  };

  // Fired by FormGradeModal after a successful save. Finalize the proposal so
  // the session records the action; the executor re-checks the grade landed.
  const handleGraded = async () => {
    if (finalizing) return;
    setGradeData(null);
    setFinalizing(true);
    try {
      const res = await postRightworkAPI(API_ops_assistant_confirm_action, {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action: "confirm",
      });
      if (res.status === 200) {
        setStatus("confirmed");
        onStatusChange?.(proposal.proposal_id, "confirmed");
      }
    } catch (error) {
      // The grade itself saved; only the proposal bookkeeping failed. Surface
      // it quietly without claiming the grade was lost.
      toast.error(
        error?.response?.data?.system?.message ||
          "Grade saved, but couldn't update the assistant. Refresh if it still shows pending."
      );
    } finally {
      setFinalizing(false);
    }
  };

  const handleDismiss = async () => {
    if (opening || finalizing) return;
    setFinalizing(true);
    try {
      const res = await postRightworkAPI(API_ops_assistant_confirm_action, {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action: "reject",
      });
      if (res.status === 200) {
        setStatus("cancelled");
        onStatusChange?.(proposal.proposal_id, "cancelled");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to dismiss."
      );
    } finally {
      setFinalizing(false);
    }
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Form graded</div>
          {summary.employee_name && (
            <div className="text-xs opacity-80">
              {summary.employee_name}
              {summary.form_name ? ` — ${summary.form_name}` : ""}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Grading dismissed</span>
      </div>
    );
  }

  return (
    <>
      <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
          <AssignmentIcon fontSize="small" className="text-violet-700" />
          <span className="text-sm font-medium text-violet-800">
            {summary.title || "Grade Form"}
          </span>
        </div>

        <div className="px-3 py-2 space-y-1">
          {summary.employee_name && (
            <div className="text-sm">
              <span className="text-gray-500">Employee: </span>
              <span className="font-medium text-gray-700">
                {summary.employee_name}
              </span>
            </div>
          )}
          {summary.form_name && (
            <div className="text-sm">
              <span className="text-gray-500">Form: </span>
              <span className="font-medium text-gray-700">
                {summary.form_name}
              </span>
            </div>
          )}
          {summary.section_name && (
            <div className="text-sm">
              <span className="text-gray-500">Section: </span>
              <span className="font-medium text-gray-700">
                {summary.section_name}
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
          <button
            onClick={handleDismiss}
            disabled={opening || finalizing}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Dismiss
          </button>
          <button
            onClick={handleOpenGrader}
            disabled={opening || finalizing}
            className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
          >
            {(opening || finalizing) && (
              <CircularProgress size={14} sx={{ color: "white" }} />
            )}
            Grade
          </button>
        </div>
      </div>

      {gradeData && (
        <FormGradeModal
          open={!!gradeData}
          onClose={() => setGradeData(null)}
          record={gradeData.record}
          submission={gradeData.submission}
          homeCompany={homeCompany}
          onGraded={handleGraded}
        />
      )}
    </>
  );
}
